import React, { useCallback, useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { autocompleteLocation, getPlaceDetails, resolveMapUrl, reverseGeocodeLatLng } from "../services/api";
import { friendlyError } from "../utils/friendlyError";
import { t } from "../i18n";

// Fix default marker icon in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Merchant uses the same Google-backed search + short-link resolution as odos_client
// via backend `/api/maps/*` endpoints.

function MapClickHandler({ onPositionSelect }) {
  useMapEvents({
    click(e) {
      onPositionSelect(e.latlng.lat, e.latlng.lng, null);
    },
  });
  return null;
}

function SetViewOnChange({ center, zoom }) {
  const map = useMap();
  React.useEffect(() => {
    if (center && center[0] != null && center[1] != null) {
      map.setView(center, zoom ?? map.getZoom());
    }
  }, [center, zoom, map]);
  return null;
}

export function DeliveryMap({ value = {}, onChange }) {
  const { deliveryAddress = "", deliveryLatitude = null, deliveryLongitude = null } = value;
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [, setSelectedPlaceId] = useState("");

  const [linkText, setLinkText] = useState("");
  const [resolvingLink, setResolvingLink] = useState(false);
  const [linkError, setLinkError] = useState("");

  const debounceRef = useRef(null);

  const hasPosition = deliveryLatitude != null && deliveryLongitude != null && Number.isFinite(deliveryLatitude) && Number.isFinite(deliveryLongitude);
  const center = hasPosition ? [deliveryLatitude, deliveryLongitude] : [9.0320, 38.7469]; // default Addis Ababa
  const zoom = hasPosition ? 15 : 10;

  // IMPORTANT: use functional update so async lookups (reverse-geocode / link resolve)
  // cannot overwrite lat/lng with stale `value`.
  const handleChange = useCallback(
    (updates) => {
      if (!onChange) return;
      onChange((prev) => ({ ...(prev || {}), ...updates }));
    },
    [onChange]
  );

  // Bumped on every new pick, so a slow lookup for an older pin can't overwrite the newer address.
  const pickSeqRef = useRef(0);

  const handlePositionSelect = useCallback(
    (lat, lng, address) => {
      const seq = ++pickSeqRef.current;
      handleChange({
        deliveryLatitude: lat,
        deliveryLongitude: lng,
        ...(address != null ? { deliveryAddress: address } : {}),
      });

      // Auto-populate address for map pin / link / manual latlng.
      if (address == null) {
        (async () => {
          try {
            const r = await reverseGeocodeLatLng(lat, lng);
            const a = (r.formattedAddress || "").trim();
            if (a && seq === pickSeqRef.current) handleChange({ deliveryAddress: a });
          } catch (e) {
            // Non-fatal: pin coordinates are already saved. Reverse-geocode needs Geocoding API
            // enabled in Google Cloud; do not show long API errors in the UI.
            console.warn("[DeliveryMap] reverse-geocode failed (optional):", e?.response?.data?.message || e?.message || e);
          }
        })();
      }
    },
    [handleChange]
  );

  // Autocomplete suggestions (debounced)
  useEffect(() => {
    const q = searchQuery.trim();
    setSearchError("");
    setSelectedPlaceId("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.length < 2) {
      setSuggestions([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const preds = await autocompleteLocation(q);
        setSuggestions(preds);
      } catch (e) {
        setSuggestions([]);
        setSearchError(friendlyError(e, t("map.search_failed")));
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  async function handleSelectSuggestion(placeId) {
    if (!placeId) return;
    setSearchError("");
    setSearching(true);
    try {
      const details = await getPlaceDetails(placeId);
      setSelectedPlaceId(placeId);
      handlePositionSelect(details.lat, details.lng, details.formattedAddress || null);
      setSuggestions([]);
    } catch (e) {
      setSearchError(friendlyError(e, t("map.place_failed")));
    } finally {
      setSearching(false);
    }
  }

  async function handleResolveLink() {
    const link = linkText.trim();
    if (!link) return;
    setLinkError("");
    setResolvingLink(true);
    try {
      const { lat, lng, formattedAddress } = await resolveMapUrl(link);
      handlePositionSelect(lat, lng, formattedAddress || null);
      setLinkText("");
    } catch (e) {
      setLinkError(friendlyError(e, t("map.link_failed")));
    } finally {
      setResolvingLink(false);
    }
  }

  // If merchant manually types lat/lng, try to auto-fill the address.
  useEffect(() => {
    const la = deliveryLatitude;
    const ln = deliveryLongitude;
    if (!Number.isFinite(la) || !Number.isFinite(ln)) return;
    if (deliveryAddress && String(deliveryAddress).trim()) return;
    const timer = setTimeout(async () => {
      try {
        const r = await reverseGeocodeLatLng(la, ln);
        const a = (r.formattedAddress || "").trim();
        if (a) handleChange({ deliveryAddress: a });
      } catch (_) {}
    }, 500);
    return () => clearTimeout(timer);
  }, [deliveryLatitude, deliveryLongitude, deliveryAddress, handleChange]);

  return (
    <div className="delivery-map-card">
      <div className="map-search">
        <i className="fa-solid fa-magnifying-glass map-search__icon" aria-hidden="true" />
        <input
          type="text"
          placeholder={t("map.search_placeholder")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label={t("map.search_aria")}
        />
        {searching ? <i className="fa-solid fa-spinner fa-spin map-search__busy" aria-hidden="true" /> : null}
        {suggestions.length > 0 && (
          <div className="map-search__suggestions" role="listbox">
            {suggestions.map((s) => (
              <button
                key={s.placeId}
                type="button"
                role="option"
                aria-selected="false"
                onClick={() => handleSelectSuggestion(s.placeId)}
              >
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
                <span>{s.description}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="map-link-row">
        <input
          type="text"
          placeholder={t("map.link_placeholder")}
          value={linkText}
          onChange={(e) => setLinkText(e.target.value)}
          aria-label={t("map.link_aria")}
        />
        <button type="button" onClick={handleResolveLink} disabled={resolvingLink || !linkText.trim()}>
          {resolvingLink ? t("map.resolving") : t("map.use_link")}
        </button>
      </div>

      {searchError ? <div className="field-error" style={{ marginBottom: 8 }}>{searchError}</div> : null}
      {linkError ? <div className="field-error" style={{ marginBottom: 8 }}>{linkError}</div> : null}

      <div className="delivery-map-frame">
        <MapContainer
          center={center}
          zoom={zoom}
          style={{ height: "100%", width: "100%" }}
          scrollWheelZoom={true}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <MapClickHandler onPositionSelect={handlePositionSelect} />
          <SetViewOnChange center={center} zoom={zoom} />
          {hasPosition && <Marker position={[deliveryLatitude, deliveryLongitude]} />}
        </MapContainer>
        {!hasPosition ? <div className="delivery-map-hint">{t("map.click_hint")}</div> : null}
      </div>

      <label className="field-label" htmlFor="delivery-address-input" style={{ marginTop: 14 }}>
        {t("map.address_label")}
      </label>
      <input
        id="delivery-address-input"
        type="text"
        placeholder={t("map.address_placeholder")}
        value={deliveryAddress}
        onChange={(e) => handleChange({ deliveryAddress: e.target.value })}
        required
      />

      <details className="coords-details">
        <summary>{t("map.manual_coords")}</summary>
        <div className="coords-grid">
          <div>
            <label className="field-label" htmlFor="delivery-lat-input">{t("map.latitude")}</label>
            <input
              id="delivery-lat-input"
              type="text"
              inputMode="decimal"
              placeholder={t("map.lat_example")}
              value={deliveryLatitude ?? ""}
              onChange={(e) => {
                const v = e.target.value.trim();
                const num = v === "" ? null : parseFloat(v);
                if (v === "" || !Number.isNaN(num)) handleChange({ deliveryLatitude: num });
              }}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="delivery-lng-input">{t("map.longitude")}</label>
            <input
              id="delivery-lng-input"
              type="text"
              inputMode="decimal"
              placeholder={t("map.lng_example")}
              value={deliveryLongitude ?? ""}
              onChange={(e) => {
                const v = e.target.value.trim();
                const num = v === "" ? null : parseFloat(v);
                if (v === "" || !Number.isNaN(num)) handleChange({ deliveryLongitude: num });
              }}
            />
          </div>
        </div>
      </details>
    </div>
  );
}
